import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Briefcase, Calendar, CheckCircle2, ArrowRight, User } from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import InstantBookingModal from '../../components/booking/InstantBookingModal';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { localized, currentLang } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    api.get(`/services/${slug}`)
      .then((res) => {
        if (res?.success) setService(res.service);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#05080e] min-h-screen py-24 text-center text-slate-400">
        Xizmat ma’lumotlari yuklanmoqda...
      </div>
    );
  }

  if (!service) {
    return (
      <div className="bg-[#05080e] min-h-screen py-24 text-center text-slate-200">
        <h2 className="text-xl font-bold font-serif mb-4">Xizmat turi topilmadi</h2>
        <Link to="/services" className="text-[#c59b27] text-sm font-semibold hover:underline">
          &larr; Barcha xizmatlarga qaytish
        </Link>
      </div>
    );
  }

  let faqs = [];
  try {
    if (Array.isArray(service.faqsJson)) {
      faqs = service.faqsJson;
    } else if (service.faqsJson && typeof service.faqsJson === 'string') {
      faqs = JSON.parse(service.faqsJson);
    }
  } catch (err) {
    faqs = [];
  }

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      {/* Banner */}
      <section className="bg-[#070c16] text-white py-16 sm:py-24 border-b border-[#141d2d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Back Navigation Bar */}
          <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-[#141d2d]">
            <button
              type="button"
              onClick={() => window.history.length > 1 ? window.history.back() : window.location.href = '/services'}
              className="inline-flex items-center space-x-1.5 text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:text-amber-200 py-1.5 px-3 rounded-lg bg-[#0b1220] border border-[#1b2b46] hover:border-[#c59b27]/60 cursor-pointer shadow-sm transition-colors"
            >
              <span>{currentLang === 'uz' ? '← Orqaga qaytish' : currentLang === 'ru' ? '← Назад' : '← Go Back'}</span>
            </button>
            <Link to="/services" className="text-xs text-slate-400 hover:text-white transition-colors">
              {currentLang === 'uz' ? 'Barcha yuridik amaliyotlar' : currentLang === 'ru' ? 'Все юридические практики' : 'All Practice Areas'}
            </Link>
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100">
            {localized(service, 'title')}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm max-w-2xl mt-4 font-light leading-relaxed">
            {localized(service, 'shortDesc')}
          </p>
        </div>
      </section>

      {/* Main Details */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Left 2 Cols: Detailed Narrative */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-8">
                <h2 className="font-serif text-2xl font-bold text-slate-100 mb-4">
                  Amaliyotning Mazmuni va Huquqiy Doirasi
                </h2>
                <div className="text-slate-300 text-xs sm:text-sm leading-relaxed space-y-4 font-light">
                  <p>{localized(service, 'fullDesc')}</p>
                </div>
              </div>

              {/* FAQs if available */}
              {faqs.length > 0 && (
                <div className="pt-8 border-t border-[#141d2d]">
                  <h3 className="font-serif text-xl font-bold text-slate-100 mb-6">
                    Ko‘p Beriladigan Savollar (FAQ)
                  </h3>
                  <div className="space-y-4">
                    {faqs.map((faq, i) => (
                      <div key={i} className="p-5 rounded-xl bg-[#090e18] border border-[#1b2b46]">
                        <h4 className="font-semibold text-slate-100 text-sm mb-2 text-[#c59b27]">
                          {faq.qUz || faq.questionUz}
                        </h4>
                        <p className="text-slate-400 text-xs leading-relaxed font-light">
                          {faq.aUz || faq.answerUz}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Col: Consultation Box */}
            <div className="space-y-6">
              <div className="bg-[#090e18] text-white p-7 rounded-xl border border-[#c59b27]/40 shadow-xl">
                <h3 className="font-serif text-lg font-bold text-[#c59b27] mb-2">
                  Konsultatsiyaga yoziling
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-6 font-light">
                  Ushbu amaliyot bo‘yicha tajribali advokat qabuliga onlayn yoziling va aniq amaliy reja oling.
                </p>

                <button
                  onClick={() => setBookingModalOpen(true)}
                  className="w-full inline-flex items-center justify-center py-3 px-4 text-xs font-bold uppercase tracking-wider text-white bg-[#b38038] hover:bg-[#c59b27] rounded shadow transition-colors cursor-pointer"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Uchrashuv vaqtini tanlash
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      <InstantBookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => setBookingModalOpen(false)} 
        preSelectedTopic={{ id: service.id, title: localized(service, 'title') }}
      />
    </div>
  );
}
